/**
 * Device detection utilities for tracking
 * Extracts OS, browser, version, and screen resolution info
 */

/**
 * Get operating system name from user agent
 * @param {string} userAgent - Navigator user agent string
 * @returns {string} OS name
 */
export function getOSName(userAgent = navigator.userAgent) {
  const ua = userAgent.toLowerCase();

  if (ua.indexOf("win") !== -1) {
    if (ua.indexOf("windows nt 10") !== -1) return "Windows 10/11";
    if (ua.indexOf("windows nt 6.3") !== -1) return "Windows 8.1";
    if (ua.indexOf("windows nt 6.2") !== -1) return "Windows 8";
    if (ua.indexOf("windows nt 6.1") !== -1) return "Windows 7";
    return "Windows";
  }

  if (ua.indexOf("macintosh") !== -1 || ua.indexOf("mac os") !== -1) {
    return "macOS";
  }

  if (ua.indexOf("linux") !== -1) {
    if (ua.indexOf("android") !== -1) return "Android";
    return "Linux";
  }

  if (ua.indexOf("iphone") !== -1 || ua.indexOf("ipad") !== -1 || ua.indexOf("ipod") !== -1) {
    return "iOS";
  }

  if (ua.indexOf("android") !== -1) {
    return "Android";
  }

  return "Unknown";
}

/**
 * Get browser name and version from user agent
 * @param {string} userAgent - Navigator user agent string
 * @returns {Object} { name, version }
 */
export function getBrowserInfo(userAgent = navigator.userAgent) {
  const ua = userAgent;
  let name = "Unknown";
  let version = "";

  // Edge (Chromium-based and Legacy)
  if (ua.indexOf("Edg/") !== -1 || ua.indexOf("Edge/") !== -1) {
    name = "Edge";
    const match = ua.match(/(?:Edg|Edge)\/([\d.]+)/);
    version = match ? match[1] : "";
  }
  // Opera
  else if (ua.indexOf("OPR/") !== -1 || ua.indexOf("Opera/") !== -1) {
    name = "Opera";
    const match = ua.match(/(?:OPR|Opera)\/([\d.]+)/);
    version = match ? match[1] : "";
  }
  // Chrome (check before Safari since Chrome also has Safari in UA)
  else if (ua.indexOf("Chrome/") !== -1 && ua.indexOf("Edg/") === -1 && ua.indexOf("OPR/") === -1) {
    name = "Chrome";
    const match = ua.match(/Chrome\/([\d.]+)/);
    version = match ? match[1] : "";
  }
  // Firefox
  else if (ua.indexOf("Firefox/") !== -1) {
    name = "Firefox";
    const match = ua.match(/Firefox\/([\d.]+)/);
    version = match ? match[1] : "";
  }
  // Safari
  else if (ua.indexOf("Safari/") !== -1 && ua.indexOf("Chrome/") === -1) {
    name = "Safari";
    const match = ua.match(/Version\/([\d.]+)/);
    version = match ? match[1] : "";
  }

  return { name, version };
}

/**
 * Get screen resolution
 * @returns {string} Resolution in format "widthxheight"
 */
export function getScreenResolution() {
  if (typeof window === "undefined" || !window.screen) {
    return "Unknown";
  }
  return `${window.screen.width}x${window.screen.height}`;
}

/**
 * Get all device information in one call
 * @returns {Object} Device info object
 */
export function getDeviceInfo() {
  if (typeof navigator === "undefined") {
    return {
      os_name: "Unknown",
      browser_name: "Unknown",
      browser_version: "",
      screen_resolution: "Unknown",
    };
  }

  const browser = getBrowserInfo();

  return {
    os_name: getOSName(),
    browser_name: browser.name,
    browser_version: browser.version,
    screen_res: getScreenResolution(),
  };
}


export default {
  getOSName,
  getBrowserInfo,
  getScreenResolution,
  getDeviceInfo
};
